import { createContext, useContext, useMemo } from 'react'
import { useGetLessonsSearchQuery } from 'util/rtk-api'

type LessonOverviewRow = {
	lessonId: string
	name: string
	assignedClassId?: string
	assignedClassName?: string
	lastViewed?: number
	lastModified?: number
	status?: string
	hasGradedResults: boolean
}

type LessonsContextType = {
	lessons: LessonOverviewRow[]
	isLoading: boolean
	isError: boolean
	retry: () => void
	query?: string
}

export const LessonsContext = createContext<LessonsContextType | null>(null)

export const useLessonsContext = () => {
	const ctx = useContext(LessonsContext)
	if (!ctx) throw new Error('useLessonsContext must be used within LessonsContextProvider')
	return ctx
}

export const LessonsContextProvider = ({
	children,
	query
}: {
	children: React.ReactNode
	query?: string
}) => {
	// err-11: isError must reach the UI — a 5xx used to render as an empty library
	const { data, isLoading, isError, refetch } = useGetLessonsSearchQuery(undefined)

	const lessons: LessonOverviewRow[] = useMemo(() => {
		const list = data?.lessons ?? []
		// No search query → return the full list unchanged
		if (!query) return list
		// Normalize query for case-insensitive matching
		const q = query?.toLowerCase() ?? ''
		// Build a loose "subsequence" regex (chars of q in order, gaps allowed)
		const regex = new RegExp(q.split('').join('.*'), 'i')
		return list
			.map(l => {
				// Score each lesson name based on how well it matches the query
				// 1) sequentialScore: reward when query characters appear in order
				let sequentialScore = 0
				let idx = 0
				const name = (l.name ?? '').toLowerCase()
				for (const ch of q) {
					// Find each character after the previous match to preserve order
					idx = name.indexOf(ch, idx)
					if (idx === -1) { sequentialScore = 0; break }
					sequentialScore += 2
					idx++
				}
				// 2) regexScore: small bonus if the loose subsequence regex matches
				const regexScore = regex.test(name) ? 1 : 0
				const score = sequentialScore + regexScore
				// Keep only positively scored entries (falsy dropped by filter(Boolean))
				return score > 0 && { entry: l, score }
			})
			.filter(Boolean)
			// Highest score first
			.sort((a: any, b: any) => b.score - a.score)
			.map((e: any) => e.entry)
	}, [data, query])

	return (
		<LessonsContext.Provider value={{ lessons, isLoading, isError, retry: refetch, query }}>
			{children}
		</LessonsContext.Provider>
	)
}


